export type HealthStatus = "ok" | "failed" | "unchecked";

export type SourcingMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
  health: { status: HealthStatus; checked_at: string | null; reason: string | null };
  served_from?: "snapshot" | "live";
  built_at?: string | null;
};

export type RefreshRun = {
  run_id: string;
  execution: string | null;
  status: "running" | "ok" | "failed";
  event_at: string;
  started_at: string;
  built_at: string | null;
  data_as_of: string | null;
  error: string | null;
};

export type RefreshStatus = {
  run: RefreshRun | null;
  built_at: string | null;
  data_as_of: string | null;
};

export type RefreshStart =
  | { status: "started"; requested_at: string }
  | { status: "already_running"; run: RefreshRun };

export type Enveloped<T> = { data: T | null; meta: SourcingMeta };
export type Page<Row> = { items: Row[]; total: number; limit: number; offset: number };

export type FilterOptions = {
  // type arrives with the sourcing-funnel API of PR 189; older responses leave it out.
  items: Array<{ item_id: string; item_name: string; funnel: string; type?: string | null }>;
  funnels: string[];
  phases: string[];
  geo: Array<{ dimension_key: string; dimension_label: string; value: string }>;
  options: Array<{ option_id: string; option_name: string }>;
  variants: string[];
  cost_types: string[];
  features: string[];
  criteria_phases: string[];
  // v1.6: the item's sample names and reviewers (sourcing-funnel API v1.5); older responses leave them out.
  samples?: string[];
  // v1.7: one entry per option x sample, labelled "Option – Sample" (sourcing-funnel API v1.6); older responses leave it out.
  sample_picks?: Array<{ key: string; option_id: string; option_name: string; sample: string; label: string }>;
  evaluators?: string[];
  criteria: Array<{ criterion: string; feature: string; criteria_phase: string }>;
};

export type ApiParams = Record<string, string | number>;

const BASE = import.meta.env.VITE_SOURCING_API_BASE ?? "/sourcing/v1";
export const STALE_TIME = 60_000;

export class SourcingApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Sourcing API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function query(params: ApiParams): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) qs.set(key, String(value));
  const search = qs.toString();
  return search ? `?${search}` : "";
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new SourcingApiError(response.status, await safeBody(response));
  return (await response.json()) as T;
}

export function getTab<T>(apiTab: string, params: ApiParams): Promise<Enveloped<T>> {
  return getJson<Enveloped<T>>(`/${apiTab}${query(params)}`);
}

export function getRefresh(): Promise<RefreshStatus> {
  return getJson<RefreshStatus>("/refresh");
}

export async function startRefresh(): Promise<RefreshStart> {
  const response = await fetch(`${BASE}/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: "{}",
  });
  if (!response.ok) throw new SourcingApiError(response.status, await safeBody(response));
  return (await response.json()) as RefreshStart;
}

/** The choices that narrow the other lists: Funnel narrows items, Funnel or Item narrows phases and geos, all narrow options. */
export interface FilterNarrowing {
  funnel?: string | null;
  phase?: string | null;
  geo?: string | null;
}

export function getFilters(item: string | null, narrow: FilterNarrowing = {}): Promise<Enveloped<FilterOptions>> {
  const params: ApiParams = {};
  if (item) params.item = item;
  for (const key of ["funnel", "phase", "geo"] as const) {
    const value = narrow[key];
    if (value) params[key] = value;
  }
  return getJson<Enveloped<FilterOptions>>(`/filters${query(params)}`);
}

function errorCode(body: unknown): string | null {
  if (body === null || typeof body !== "object") return null;
  const code = (body as { error?: unknown }).error;
  return typeof code === "string" ? code : null;
}

export function messageFor(error: unknown, areaName: string): string {
  if (!(error instanceof SourcingApiError)) return "We can't reach the sourcing data right now. Try again shortly.";
  switch (error.status) {
    case 400:
      return "This page was asked for something it can't show. Clear the filters and try again.";
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You don't have access. Ask the ${areaName} area owner.`;
    case 503:
      return errorCode(error.body) === "not_configured"
        ? "The sourcing data address is not configured on the portal, so nothing is shown."
        : "We can't reach the sourcing data right now. Try again shortly.";
    default:
      return "We can't reach the sourcing data right now. Try again shortly.";
  }
}
